import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { FileUploadResponse } from 'src/app/core/models/file-upload-results.model';
import { InterceptSummaryResponse } from 'src/app/core/models/position-operations/intercepts.model';
import {
  CountryLimitResponse,
  RevenueRecoveryRequest,
  RevenueRecoveryResponse,
} from 'src/app/core/models/revenue-recovery/revenue-recovery';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class RevenueRecoveryService {
  private readonly _http = inject(HttpClient);
  private readonly _commonUtils = inject(CommonUtils);
  private readonly _errorHandler = inject(ErrorhandleService);

  readonly baseUrl = environment.apiUrl.concat('/revenue-recovery');

  readonly interceptBaseUrl = environment.apiUrl;

  /**
   *Get revenue Recovery
   * @return {*}  {Observable<SortCodeResponse>}
   * @memberof RevenueRecoveryService
   */
  getRevenueRecovery(
    revenueRecoveryRequest: RevenueRecoveryRequest,
    audit: number,
  ): Observable<RevenueRecoveryResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<RevenueRecoveryResponse>(
        `${this.baseUrl}/?audit=${audit}`,
        revenueRecoveryRequest,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<RevenueRecoveryResponse>(
            error,
            error.error.code,
            'Revenue Recovery Service: get Revenue Recovery',
            BANNER_TYPE.API_400_ERROR,
            {} as RevenueRecoveryResponse,
          ),
        ),
        filter((val) => !!val),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get Intercepts
   * @return {*}  {Observable<InterceptSummaryResponse>}
   * @memberof InterceptsService
   */
  getIntercepts(_suppressBanner = false): Observable<InterceptSummaryResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<InterceptSummaryResponse>(
        this.interceptBaseUrl.concat('/intercept/get-intercepts'),
      )
      .pipe(
        take(1),
        catchError((error) => {
          if (
            error.error.code === 400 &&
            error.error.message === 'No Data Available'
          ) {
            return of(null as unknown as InterceptSummaryResponse);
          } else {
            return this._errorHandler.handleError<InterceptSummaryResponse>(
              error,
              error.error.code,
              'RevenueRecovery Service: Get Intercepts',
              BANNER_TYPE.TABLE_DATA_ERROR,
            );
          }
        }),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * revenue Recovery file upload
   * @return {*}  {Observable<SortCodeResponse>}
   * @memberof RevenueRecoveryService
   */
  revenueRecoveryFileUpload(
    revenueRecoveryRequest: RevenueRecoveryRequest,
    audit: number,
  ): Observable<FileUploadResponse> {
    return this._http
      .post<FileUploadResponse>(
        `${this.baseUrl}/file-upload?audit=${audit}`,
        revenueRecoveryRequest,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<FileUploadResponse>(
            error,
            error.error.code,
            'Revenue Recovery Service: Revenue Recovery File upload',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
      );
  }

  /**
   *Get country limits
   * @return {*}  {Observable<CountryLimitResponse>}
   * @memberof RevenueRecoveryService
   */
  getCountryLimits(): Observable<CountryLimitResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<CountryLimitResponse>(`${this.baseUrl}/country-limits/`)
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<CountryLimitResponse>(
            error,
            error.error.code,
            'Revenue Recovery Service: get Country Limits',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
